/**
 * Bulk Events Form Component
 * 
 * Main form for configuring and creating bulk events.
 * Includes date range, recurrence pattern, and day configurations.
 * 
 * Requirements: 1.1, 1.2, 2.1, 2.2, 3.1-3.12
 */

import React from 'react';
import { Button } from '../../common/Button';
import { Card } from '../../common/Card';
import { FormField } from '../../common/FormField';
import { DayConfiguration } from './DayConfiguration';
import type { BulkEventsConfig } from '../../../utils/bulkEventsValidation';
import type { EventType, Location } from '../../../api/rest-client';

export interface BulkEventsFormProps {
  config: BulkEventsConfig;
  eventTypes: EventType[];
  locations: Location[];
  errors: Record<string, any>;
  isLoading?: boolean;
  onConfigChange: (config: BulkEventsConfig) => void;
  onPreview: () => void;
  onCreate: () => void;
}

const RECURRENCE_OPTIONS = [
  { value: '1', label: 'Every week' },
  { value: '2', label: 'Every 2 weeks' },
  { value: '3', label: 'Every 3 weeks' },
  { value: '4', label: 'Every 4 weeks' },
];

/**
 * Bulk Events Form Component
 * Requirements: 1.1, 1.2, 2.1, 2.2, 3.1-3.12
 */
export function BulkEventsForm({
  config,
  eventTypes,
  locations,
  errors,
  isLoading = false,
  onConfigChange,
  onPreview,
  onCreate,
}: BulkEventsFormProps) {
  const handleDateChange = (field: 'startDate' | 'endDate') => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    onConfigChange({
      ...config,
      [field]: e.target.value,
    });
  };
  
  const handleRecurrenceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    onConfigChange({
      ...config,
      recurrenceWeeks: parseInt(e.target.value, 10),
    });
  };
  
  const handleDayConfigChange = (dayOfWeek: number) => (dayConfig: any) => {
    const newSchedule = [...config.weeklySchedule];
    newSchedule[dayOfWeek] = dayConfig;
    onConfigChange({
      ...config,
      weeklySchedule: newSchedule,
    });
  };
  
  const hasEnabledDays = config.weeklySchedule.some(day => day.enabled);
  
  return (
    <div className="space-y-6">
      {/* Date Range Section */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Range</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Start Date"
            name="startDate"
            type="date"
            value={config.startDate}
            error={errors.startDate}
            touched={true}
            required
            onChange={handleDateChange('startDate')}
          />
          
          <FormField
            label="End Date"
            name="endDate"
            type="date"
            value={config.endDate}
            error={errors.endDate}
            touched={true}
            required
            onChange={handleDateChange('endDate')}
          />
        </div>
      </Card>
      
      {/* Recurrence Pattern Section */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recurrence Pattern</h3>
        <FormField
          label="Repeat"
          name="recurrenceWeeks"
          type="select"
          value={config.recurrenceWeeks.toString()}
          required
          options={RECURRENCE_OPTIONS}
          onChange={handleRecurrenceChange}
        />
      </Card>
      
      {/* Weekly Schedule Section */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Schedule</h3>
        <p className="text-sm text-gray-600 mb-4">
          Select which days of the week to create events and configure each day's details.
        </p>
        <div className="space-y-3">
          {/* Display days starting with Monday (1) through Sunday (0) */}
          {[1, 2, 3, 4, 5, 6, 0].map((dayOfWeek) => (
            <DayConfiguration
              key={dayOfWeek}
              dayOfWeek={dayOfWeek}
              config={config.weeklySchedule[dayOfWeek]}
              eventTypes={eventTypes}
              locations={locations}
              errors={errors[`day${dayOfWeek}`] || {}}
              onChange={handleDayConfigChange(dayOfWeek)}
            />
          ))}
        </div>
      </Card>
      
      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <Button
          variant="secondary"
          onClick={onPreview}
          disabled={isLoading || !hasEnabledDays}
        >
          Preview Events
        </Button>
        
        <Button
          variant="primary"
          onClick={onCreate}
          disabled={isLoading || !hasEnabledDays}
        >
          {isLoading ? 'Creating...' : 'Create Events'}
        </Button>
      </div>
    </div>
  );
}

export default BulkEventsForm;
